// src/views/admin/WorkOrderLiveMetrics.js
// FlowSense MOW+ — Real-Time Work Order Metrics Panel
//
// Enterprise live KPI panel driven by three data sources:
//   1. webSocketService  : 'watchdog_events' + 'work_order_events' → instant updates
//   2. Watchdog REST API : GET /api/v1/workorders/watchdog/metrics  → authoritative counts
//   3. ML API            : aiService.getAIHealthMetrics()           → circuit + model status
//
// This component is intentionally self-contained so it can be placed anywhere
// in the dashboard without coupling to parent state. It exposes a single prop:
//   onCircuitChange?(state: 'CLOSED'|'HALF_OPEN'|'OPEN') — optional callback
//   so the parent can react to circuit-breaker state changes.

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  memo,
} from 'react';
import {
  Box, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText, StatArrow,
  Badge, HStack, VStack, Text, Icon, Progress, Flex, Heading,
  Tooltip, CircularProgress, CircularProgressLabel, Skeleton,
  Divider, useColorModeValue, IconButton,
} from '@chakra-ui/react';
import {
  MdTrendingUp, MdTrendingDown, MdCheckCircle, MdError, MdWarning,
  MdSpeed, MdAutorenew, MdBolt, MdOutlineHealthAndSafety,
} from 'react-icons/md';
import { FaShieldAlt, FaRobot, FaExclamationTriangle, FaBolt } from 'react-icons/fa';
import { motion } from 'framer-motion';

import webSocketService  from '../../services/websocketService';
import analyticsService  from '../../services/analyticsService';
import aiService         from '../../services/aiService';
import api               from '../../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 60_000; // Authoritative REST poll every 60 s

const CIRCUIT_SCHEME = {
  CLOSED:    'green',
  HALF_OPEN: 'yellow',
  OPEN:      'red',
};

const MotionBox = motion(Box);

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENT: ANIMATED KPI CARD
// ─────────────────────────────────────────────────────────────────────────────

const KpiCard = memo(({
  label, value, sub, colorScheme = 'blue', icon, isLoading,
  trend = null, accentGradient,
}) => {
  const bg      = useColorModeValue('white',    'gray.800');
  const border  = useColorModeValue('gray.200', 'gray.700');
  const sub_    = useColorModeValue('gray.500', 'gray.400');
  
  const iconBg  = useColorModeValue(`${colorScheme}.50`, `${colorScheme}.900`);

  return (
    <MotionBox
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      <Box
        bg={bg}
        borderWidth="1px"
        borderColor={border}
        borderRadius="xl"
        p={4}
        position="relative"
        overflow="hidden"
        _hover={{ boxShadow: 'md', transform: 'translateY(-1px)', transition: 'all 0.2s' }}
        transition="all 0.2s"
      >
        {accentGradient && (
          <Box
            position="absolute" top={0} left={0} right={0} h="3px"
            bgGradient={accentGradient}
          />
        )}
        <Flex justify="space-between" align="flex-start">
          <Stat size="sm">
            <StatLabel fontSize="xs" fontWeight="700" textTransform="uppercase" color={sub_}>
              {label}
            </StatLabel>
            {isLoading ? (
              <Skeleton height="28px" width="60px" mt={1} />
            ) : (
              <StatNumber fontSize="2xl" fontWeight="800" color={`${colorScheme}.500`}>
                {value}
              </StatNumber>
            )}
            {sub && !isLoading && (
              <StatHelpText mb={0} mt={0.5} fontSize="xs">
                {trend != null && (
                  <StatArrow type={trend >= 0 ? 'increase' : 'decrease'} />
                )}
                {sub}
              </StatHelpText>
            )}
          </Stat>
          {icon && (
            <Box
              p={2}
              bg={iconBg}
              borderRadius="md"
              flexShrink={0}
            >
              <Icon as={icon} color={`${colorScheme}.500`} boxSize={5} />
            </Box>
          )}
        </Flex>
      </Box>
    </MotionBox>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENT: SLA COMPLIANCE GAUGE
// ─────────────────────────────────────────────────────────────────────────────

const SlaGauge = memo(({ compliance, isLoading }) => {
  const bg     = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const sub    = useColorModeValue('gray.500', 'gray.400');
  
  const trackColor = useColorModeValue('gray.100', 'gray.700');

  const color = compliance >= 90 ? 'green' : compliance >= 70 ? 'yellow' : 'red';

  return (
    <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4}>
      <Text fontSize="xs" fontWeight="700" textTransform="uppercase" color={sub} mb={3}>
        SLA Compliance
      </Text>
      <Flex align="center" justify="space-between">
        {isLoading ? (
          <Skeleton height="80px" width="80px" borderRadius="full" />
        ) : (
          <CircularProgress
            value={compliance}
            color={`${color}.400`}
            trackColor={trackColor}
            size="80px"
            thickness="10px"
          >
            <CircularProgressLabel fontSize="md" fontWeight="800">
              {compliance}%
            </CircularProgressLabel>
          </CircularProgress>
        )}
        <VStack align="flex-start" spacing={1} flex={1} pl={4}>
          <Badge colorScheme={color} borderRadius="full" px={2}>
            {compliance >= 90 ? 'Compliant' : compliance >= 70 ? 'At Risk' : 'Critical'}
          </Badge>
          <Text fontSize="xs" color={sub}>
            {compliance >= 90
              ? 'All SLA targets met'
              : `${100 - compliance}% of WOs breached`}
          </Text>
        </VStack>
      </Flex>
    </Box>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENT: ML CIRCUIT BREAKER STATUS
// ─────────────────────────────────────────────────────────────────────────────

const CircuitBreakerCard = memo(({ circuitState, failCount, isLoading }) => {
  const bg      = useColorModeValue('white', 'gray.800');
  const border  = useColorModeValue('gray.200', 'gray.700');
  const sub     = useColorModeValue('gray.500', 'gray.400');
  const scheme  = CIRCUIT_SCHEME[circuitState] || 'gray';
  const isOpen  = circuitState === 'OPEN';

  const pulseAnim = isOpen
    ? { boxShadow: ['0 0 0px rgba(229,62,62,0)', '0 0 12px rgba(229,62,62,0.5)', '0 0 0px rgba(229,62,62,0)'], transition: { duration: 1.6, repeat: Infinity } }
    : {};

  return (
    <MotionBox animate={pulseAnim}>
      <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4}>
        <HStack justify="space-between" mb={2}>
          <Text fontSize="xs" fontWeight="700" textTransform="uppercase" color={sub}>
            ML Circuit Breaker
          </Text>
          <Icon as={FaRobot} color="purple.400" boxSize={4} />
        </HStack>
        {isLoading ? (
          <Skeleton height="32px" />
        ) : (
          <>
            <HStack spacing={2}>
              <Box
                w="10px" h="10px" borderRadius="full"
                bg={`${scheme}.400`}
                boxShadow={isOpen ? `0 0 8px var(--chakra-colors-${scheme}-400)` : 'none'}
              />
              <Text fontWeight="800" fontSize="lg" color={`${scheme}.500`}>
                {circuitState}
              </Text>
            </HStack>
            {failCount > 0 && (
              <Text fontSize="xs" color="red.400" mt={1}>
                {failCount} consecutive failure{failCount !== 1 ? 's' : ''}
              </Text>
            )}
            <Text fontSize="xs" color={sub} mt={1}>
              {circuitState === 'CLOSED'   ? 'AI predictions routing normally'    :
               circuitState === 'HALF_OPEN'? 'Probing — one successful call closes' :
               'Predictions paused, fallback active'}
            </Text>
          </>
        )}
      </Box>
    </MotionBox>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENT: RECENT WATCHDOG CORRECTIONS
// ─────────────────────────────────────────────────────────────────────────────

const CorrectionsLog = memo(({ corrections, timestamp, isLoading }) => {
  const bg     = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const sub    = useColorModeValue('gray.500', 'gray.400');
  const rowBg  = useColorModeValue('gray.50', 'gray.750');

  const entries = useMemo(() => {
    if (!corrections) return [];
    return Object.entries(corrections)
      .filter(([, v]) => typeof v === 'number' && v > 0)
      .map(([k, v]) => ({ label: k.replace(/_/g, ' '), value: v }));
  }, [corrections]);

  return (
    <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4}>
      <HStack justify="space-between" mb={3}>
        <Text fontSize="xs" fontWeight="700" textTransform="uppercase" color={sub}>
          Last Watchdog Scan
        </Text>
        {timestamp && (
          <Text fontSize="2xs" color={sub}>
            {new Date(timestamp).toLocaleTimeString()}
          </Text>
        )}
      </HStack>
      {isLoading ? (
        <VStack spacing={2}>
          {[1, 2, 3].map(i => <Skeleton key={i} height="18px" />)}
        </VStack>
      ) : entries.length === 0 ? (
        <Flex align="center" gap={2}>
          <Icon as={MdCheckCircle} color="green.400" boxSize={4} />
          <Text fontSize="sm" color="green.500" fontWeight="600">No corrections needed</Text>
        </Flex>
      ) : (
        <VStack align="stretch" spacing={1}>
          {entries.map(({ label, value }) => (
            <Flex
              key={label}
              justify="space-between"
              align="center"
              px={2}
              py={1}
              bg={rowBg}
              borderRadius="md"
            >
              <Text fontSize="xs" textTransform="capitalize" color={sub}>{label}</Text>
              <Badge
                colorScheme={label.includes('breached') ? 'red' : label.includes('at_risk') ? 'orange' : 'blue'}
                borderRadius="full"
                fontSize="2xs"
              >
                {value}
              </Badge>
            </Flex>
          ))}
        </VStack>
      )}
    </Box>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

// --- UPDATE: Added dateRange prop ---
const WorkOrderLiveMetrics = ({ onCircuitChange, dateRange = {} }) => {
  const mountedRef = useRef(true);
  const pollerRef  = useRef(null);

  // --- FIX: Stabilize fallback dates so they don't change on every render ---
  const fallbackDates = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    return { start, end };
  }, []);

  let { start: startDate, end: endDate } = dateRange;
  if (!startDate || !endDate) {
    startDate = startDate || fallbackDates.start;
    endDate = endDate || fallbackDates.end;
  }
  
  const startStr = typeof startDate === 'object' && startDate ? startDate.toISOString() : String(startDate || '');
  const endStr = typeof endDate === 'object' && endDate ? endDate.toISOString() : String(endDate || '');
  // --- END UPDATE ---

  // ── State ──────────────────────────────────────────────────────────────────
  const [liveStats, setLiveStats] = useState({
    totalOpen:          0,
    breached:           0,
    atRisk:             0,
    stagnant:           0,
    unassignedCritical: 0,
    efficiency:         100,
    healthLabel:        'excellent',
  });
  const [lastCorrections, setLastCorrections] = useState(null);
  const [lastScanAt,      setLastScanAt]      = useState(null);
  const [mlHealth,        setMlHealth]        = useState(null);
  const [circuitState,    setCircuitState]    = useState('CLOSED');
  const [circuitFailCount,setCircuitFailCount]= useState(0);
  const [isLoading,       setIsLoading]       = useState(true);
  const [prevEfficiency,  setPrevEfficiency]  = useState(null);

  // Keep a ref so WS handlers can read current circuitState without stale closure
  const circuitRef = useRef('CLOSED');

  const updateCircuit = useCallback((newState, failCount = 0) => {
    circuitRef.current = newState;
    setCircuitState(newState);
    setCircuitFailCount(failCount);
    onCircuitChange?.(newState);
  }, [onCircuitChange]);

  // ── REST fetch (authoritative source, polled every 60 s) ──────────────────
  const fetchMetrics = useCallback(async () => {
    try {
      // --- UPDATE: Pass the stable date strings as query params ---
      const params = { startDate: startStr, endDate: endStr };

      const [metricsRes, mlRes, circuitRes] = await Promise.allSettled([
        api.get('/workorders/watchdog/metrics', { params }),
        aiService.getAIHealthMetrics().catch(() => null),
        api.get('/workorders/watchdog/circuit', { params }).catch(() => null),
      ]);
      // --- END UPDATE ---

      if (!mountedRef.current) return;

      if (metricsRes.status === 'fulfilled' && metricsRes.value?.data?.success) {
        const d = metricsRes.value.data.data;
        setPrevEfficiency(prev => prev ?? d.efficiency);
        setLiveStats(d);
      }

      if (mlRes.status === 'fulfilled' && mlRes.value) {
        setMlHealth(mlRes.value);
      }

      if (circuitRes.status === 'fulfilled' && circuitRes.value?.data?.success) {
        const c = circuitRes.value.data.data;
        updateCircuit(c.state, c.fail_count);
      }

      setIsLoading(false);
    } catch {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [updateCircuit, startStr, endStr]); // --- UPDATE: Added startStr, endStr to dependency array ---

  // ── Mount / unmount / Tab Switch Trigger ───────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    fetchMetrics(); // This will re-trigger whenever startStr/endStr change (i.e. tab switches or page loads)
    pollerRef.current = setInterval(fetchMetrics, POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      if (pollerRef.current) clearInterval(pollerRef.current);
    };
  }, [fetchMetrics]);

  // ── WebSocket subscriptions ────────────────────────────────────────────────
  useEffect(() => {
    // Handler 1: Watchdog events → instant metric update on scan_complete
    const onWatchdog = (payload) => {
      if (!mountedRef.current) return;
      const type = payload?.type;

      if (type === 'scan_complete') {
        const c = payload?.corrections ?? {};
        setLastCorrections(c);
        setLastScanAt(payload?.timestamp ?? new Date().toISOString());
        if (payload?.efficiency_rating != null) {
          setPrevEfficiency(liveStats.efficiency);
          setLiveStats(prev => ({ ...prev, efficiency: payload.efficiency_rating }));
        }
        // Pull fresh authoritative counts silently
        fetchMetrics();
      }

      if (type === 'circuit_open')   updateCircuit('OPEN',    payload?.fail_count ?? circuitFailCount + 1);
      if (type === 'circuit_closed') updateCircuit('CLOSED',  0);
    };

    // Handler 2: Work order events → re-poll on meaningful changes
    const onWorkOrder = (payload) => {
      if (!mountedRef.current) return;
      const hot = ['sla_watchdog_update', 'unassigned_critical', 'stagnant_flagged', 'ai_neglect_retriggered'];
      if (hot.includes(payload?.type)) fetchMetrics();
    };

    webSocketService?.subscribe?.('watchdog_events',   onWatchdog);
    webSocketService?.subscribe?.('work_order_events', onWorkOrder);

    return () => {
      webSocketService?.unsubscribe?.('watchdog_events',   onWatchdog);
      webSocketService?.unsubscribe?.('work_order_events', onWorkOrder);
    };
  }, [fetchMetrics, updateCircuit, circuitFailCount, liveStats.efficiency]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const slaCompliance = useMemo(() => {
    const total   = liveStats.totalOpen ?? 0;
    const breached = liveStats.breached  ?? 0;
    return total > 0 ? Math.max(0, Math.round(((total - breached) / total) * 100)) : 100;
  }, [liveStats]);

  const efficiencyTrend = useMemo(() => {
    if (prevEfficiency == null) return null;
    return liveStats.efficiency - prevEfficiency;
  }, [liveStats.efficiency, prevEfficiency]);

  // ── Theme ─────────────────────────────────────────────────────────────────
  const bg       = useColorModeValue('white', 'gray.800');
  const border   = useColorModeValue('gray.200', 'gray.700');
  const sub      = useColorModeValue('gray.500', 'gray.400');
  const heading  = useColorModeValue('gray.800', 'white');

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Section header */}
      <HStack mb={4} spacing={2}>
        <Icon as={FaBolt} color="yellow.400" boxSize={4} />
        <Heading size="sm" color={heading}>Live Operations Pulse</Heading>
        <Badge colorScheme="green" fontSize="2xs" borderRadius="full" variant="subtle">
          ● LIVE
        </Badge>
        <Tooltip label="Driven by WebSocket events + 60s REST poll" hasArrow>
          <Box display="inline-block"> {/* Ensure ref propagates safely */}
            <Icon as={MdOutlineHealthAndSafety} color="gray.400" boxSize={4} cursor="help" />
          </Box>
        </Tooltip>
        <IconButton
          icon={<MdAutorenew />}
          size="xs"
          variant="ghost"
          aria-label="Refresh metrics"
          onClick={fetchMetrics}
          isDisabled={isLoading}
          ml="auto"
        />
      </HStack>

      {/* Row 1: Core KPI cards */}
      <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={3} mb={4}>
        <KpiCard
          label="Open Work Orders"
          value={liveStats.totalOpen ?? 0}
          sub="currently active"
          colorScheme="blue"
          icon={MdSpeed}
          isLoading={isLoading}
          accentGradient="linear(to-r, blue.400, cyan.400)"
        />
        <KpiCard
          label="SLA Breached"
          value={liveStats.breached ?? 0}
          sub="past deadline"
          colorScheme={liveStats.breached > 0 ? 'red' : 'green'}
          icon={liveStats.breached > 0 ? MdError : MdCheckCircle}
          isLoading={isLoading}
          accentGradient="linear(to-r, red.400, orange.400)"
        />
        <KpiCard
          label="At Risk"
          value={liveStats.atRisk ?? 0}
          sub="approaching deadline"
          colorScheme="orange"
          icon={MdWarning}
          isLoading={isLoading}
          accentGradient="linear(to-r, orange.400, yellow.400)"
        />
        <KpiCard
          label="Unassigned Critical"
          value={liveStats.unassignedCritical ?? 0}
          sub="no owner assigned"
          colorScheme={liveStats.unassignedCritical > 0 ? 'red' : 'green'}
          icon={FaExclamationTriangle}
          isLoading={isLoading}
          accentGradient="linear(to-r, red.500, pink.400)"
        />
        <KpiCard
          label="Efficiency"
          value={`${liveStats.efficiency ?? 100}%`}
          sub={efficiencyTrend != null ? `${efficiencyTrend > 0 ? '+' : ''}${efficiencyTrend.toFixed(1)} pts` : 'from last scan'}
          colorScheme={liveStats.efficiency >= 90 ? 'green' : liveStats.efficiency >= 70 ? 'yellow' : 'red'}
          icon={liveStats.efficiency >= 75 ? MdTrendingUp : MdTrendingDown}
          trend={efficiencyTrend}
          isLoading={isLoading}
          accentGradient="linear(to-r, green.400, teal.400)"
        />
      </SimpleGrid>

      {/* Row 2: Gauges + ML status + corrections */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
        <SlaGauge compliance={slaCompliance} isLoading={isLoading} />

        {/* Watchdog efficiency gauge */}
        <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4}>
          <Text fontSize="xs" fontWeight="700" textTransform="uppercase" color={sub} mb={3}>
            Watchdog Efficiency
          </Text>
          {isLoading ? <Skeleton height="80px" /> : (
            <>
              <Progress
                value={liveStats.efficiency ?? 100}
                colorScheme={liveStats.efficiency >= 90 ? 'green' : liveStats.efficiency >= 70 ? 'yellow' : 'red'}
                size="lg"
                borderRadius="full"
                hasStripe
                isAnimated
                mb={2}
              />
              <Flex justify="space-between" fontSize="xs" color={sub}>
                <Text>0%</Text>
                <Badge
                  colorScheme={liveStats.healthLabel === 'excellent' ? 'green' : liveStats.healthLabel === 'good' ? 'teal' : 'red'}
                  textTransform="capitalize"
                >
                  {liveStats.healthLabel ?? 'fair'}
                </Badge>
                <Text>100%</Text>
              </Flex>
            </>
          )}
        </Box>

        <CircuitBreakerCard
          circuitState={circuitState}
          failCount={circuitFailCount}
          isLoading={isLoading}
        />

        <CorrectionsLog
          corrections={lastCorrections}
          timestamp={lastScanAt}
          isLoading={isLoading}
        />
      </SimpleGrid>
    </Box>
  );
};

export default memo(WorkOrderLiveMetrics);